import { branding } from "@foresting-tomorrow/api_types_firestore";
import z from "zod";

export const brandingPackageSchema = branding.brandingProperties.brandingPackageSchema;

export type BrandingPackageSchemaType = z.infer<
  typeof brandingPackageSchema
>;

export type InternalAppType = "forsler" | "skovkortet" | "george";

export type BrandingAssetKey = z.infer<typeof branding.brandingProperties.brandingAssetKey>;
export type BrandingAssetVariantSchema = z.infer<
  typeof branding.brandingProperties.brandingAssetVariantSchema
>;

export type TopLevelKey = "name" | "status";
export type SectionKey = "display" | "theme" | "links" | "legal" | "runtimeHints";

export type SectionMap = {
  display: NonNullable<BrandingPackageSchemaType["display"]>;
  theme: NonNullable<BrandingPackageSchemaType["theme"]>;
  links: NonNullable<BrandingPackageSchemaType["links"]>;
  legal: NonNullable<BrandingPackageSchemaType["legal"]>;
  runtimeHints: NonNullable<BrandingPackageSchemaType["runtimeHints"]>;
};

export type UpdateBrandingPackageType = {
  orgId: string;
  brandingPackageId: string;
  type: "top" | "section";
  key: TopLevelKey | SectionKey;
  fieldKey?: string; // only needed for section updates, to specify which field within the section to update
  value: BrandingPackageSchemaType[TopLevelKey] | SectionMap[SectionKey];
};

export type BrandingScope = "default" | "url" | "sub" | "org";
export type BrandingStatus = "idle" | "loading" | "ready" | "error";

export type UiBranding = {
  appName: string;
  shortName: string;
  tagline: string;

  primaryColor: string;
  secondaryColor: string;
  preferredLogoVariant: BrandingAssetVariantSchema;

  links?: BrandingPackageSchemaType["links"];

  legal?: BrandingPackageSchemaType["legal"];

  runtimeHints?: { allowedHosts?: string[] };

  assets: Partial<
    Record<
      BrandingAssetKey,
      {
        url: string;
        path?: string;
        contentType?: string;
        lastModifiedDate?: string;
        widthPx?: number;
        heightPx?: number;
      }
    >
  >;

  version?: number;
  lastModifiedDate?: string;
};

export type BrandingState = {
  scope: BrandingScope;
  status: BrandingStatus;
  brandingPackageId: string | null;

  ui: UiBranding;

  // Default asset URLs to use as fallbacks if branding package doesn't include specific assets - this allows us to show correct default logo/icon based on internal app type, instead of always falling back to George assets
  defaultLogoIconUrl: string;
  defaultPrintHeaderLogoUrl: string;
  // optional debug
  raw?: BrandingPackageSchemaType | null;
  error?: string;
};

export type ApplyBrandingPayload = {
  scope: BrandingScope;
  brandingPackageId: string | null;
  raw: BrandingPackageSchemaType | null;
  internalAppType?: InternalAppType;
};